import React, { useState } from 'react';
import {
    Table,
    Tag,
    Button,
    Select,
    Tabs,
    Spin,
    Typography,
    Space,
    Card,
    Row,
    Col,
    Modal,
    Form,
    message,
    Popconfirm,
    Tooltip,
    Empty,
} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    UserAddOutlined,
    ReloadOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { listFollowUps, updateFollowUp } from '../../../config/services/patrolService';
import { getAllUsers } from '../../../config/getApis';
import { parseApiError } from '../../../config/services/errorHandler';
import { useSelector } from 'react-redux';
import useColors from '../../../hooks/useColors';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;

const STATUS_TABS = ['open', 'acknowledged', 'resolved', 'cancelled'];
const STATUS_COLORS = {
    open: 'red',
    acknowledged: 'orange',
    resolved: 'green',
    cancelled: 'default',
};

/**
 * FollowUpsPage — Checkpoint follow-up work queue.
 * Route: /checkpoint-follow-ups
 */
const FollowUpsPage = () => {
    const { colorPrimary } = useColors();
    const queryClient = useQueryClient();
    const user = useSelector((state) => state.AppReducer?.user);
    const isRole = user?.userRole?.[0];
    const canWrite = isRole === 'admin' || isRole === 'manager' || isRole === 'supervisor';

    const [activeTab, setActiveTab] = useState('open');
    const [page, setPage] = useState(1);
    const [updateModal, setUpdateModal] = useState({ open: false, record: null });
    const [form] = Form.useForm();

    const filtersKey = { status: activeTab, page, perPage: 20 };

    const { data, isLoading, refetch } = useQuery(
        ['checkpoint-follow-ups', filtersKey],
        () => listFollowUps(filtersKey),
        {
            keepPreviousData: true,
            onError: (err) => {
                message.error(parseApiError(err).message);
            },
        }
    );

    const { data: userOptions = [] } = useQuery('allGuardUsers', () =>
        getAllUsers({ guard: true, supervisor: true, manager: true })
    );

    const rows = data?.rows ?? [];
    const total = data?.total ?? 0;

    // ── Update mutation ───────────────────────────────────────
    const updateMutation = useMutation(
        ({ id, body }) => updateFollowUp(id, body),
        {
            onSuccess: () => {
                message.success('Follow-up updated.');
                queryClient.invalidateQueries(['checkpoint-follow-ups']);
                setUpdateModal({ open: false, record: null });
                form.resetFields();
            },
            onError: (err) => {
                message.error(parseApiError(err).message);
            },
        }
    );

    const openUpdateModal = (record) => {
        setUpdateModal({ open: true, record });
        form.setFieldsValue({
            status: record.status,
            assignedTo: record.assignedTo ?? undefined,
        });
    };

    const handleUpdate = (values) => {
        updateMutation.mutate({
            id: updateModal.record.id,
            body: {
                status: values.status,
                ...(values.assignedTo ? { assignedTo: values.assignedTo } : {}),
            },
        });
    };

    const columns = [
        {
            title: 'Checkpoint',
            key: 'checkpoint',
            render: (_, r) => <Text>{r.checkpoint?.name ?? `#${r.checkpointId}`}</Text>,
        },
        {
            title: 'Summary',
            dataIndex: 'summary',
            key: 'summary',
            render: (s) => <Text style={{ fontSize: 12 }}>{s}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (s) => <Tag color={STATUS_COLORS[s] ?? 'default'}>{s?.toUpperCase()}</Tag>,
        },
        {
            title: 'Assigned To',
            key: 'assignedTo',
            render: (_, r) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {r.assignedTo ? `User #${r.assignedTo}` : '—'}
                </Text>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (t) => dayjs(t).format('DD MMM YYYY, HH:mm'),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, r) =>
                canWrite && (
                    <Tooltip title="Update Status / Assign">
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => openUpdateModal(r)}
                        />
                    </Tooltip>
                ),
        },
    ];

    const tabItems = STATUS_TABS.map((s) => ({
        key: s,
        label: (
            <span>
                <Tag color={STATUS_COLORS[s]} style={{ marginRight: 4 }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                </Tag>
            </span>
        ),
        children: (
            <Spin spinning={isLoading}>
                {rows.length === 0 && !isLoading ? (
                    <Empty description={`No ${s} follow-ups`} />
                ) : (
                    <Table
                        dataSource={rows}
                        columns={columns}
                        rowKey="id"
                        size="small"
                        scroll={{ x: 700 }}
                        pagination={{
                            current: page,
                            pageSize: 20,
                            total,
                            showTotal: (t) => `${t} items`,
                            onChange: (p) => setPage(p),
                        }}
                    />
                )}
            </Spin>
        ),
    }));

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <Text style={{ fontSize: 20, fontWeight: 700 }}>Checkpoint Follow-ups</Text>
                    <Text type="secondary" style={{ marginLeft: 10, fontSize: 13 }}>Work queue</Text>
                </div>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={refetch}
                    style={{
                        height: '40px',
                        borderRadius: '8px',
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        borderColor: '#d9d9d9',
                        boxShadow: 'none',
                    }}
                >
                    Refresh
                </Button>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={(key) => { setActiveTab(key); setPage(1); }}
                items={tabItems}
                tabBarStyle={{ marginBottom: 16 }}
            />

            {/* Update modal */}
            <Modal
                title="Update Follow-up"
                open={updateModal.open}
                onCancel={() => { setUpdateModal({ open: false, record: null }); form.resetFields(); }}
                footer={null}
                width={440}
                destroyOnClose
            >
                {updateModal.record && (
                    <>
                        <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12 }}>
                            <Text strong>{updateModal.record.summary}</Text>
                            {updateModal.record.details && (
                                <p style={{ margin: '6px 0 0', color: '#6b7280' }}>{updateModal.record.details}</p>
                            )}
                        </div>

                        <Form form={form} layout="vertical" onFinish={handleUpdate}>
                            <Form.Item
                                label="New Status"
                                name="status"
                                rules={[{ required: true, message: 'Status is required.' }]}
                            >
                                <Select>
                                    <Option value="open">Open</Option>
                                    <Option value="acknowledged">Acknowledged</Option>
                                    <Option value="resolved">Resolved</Option>
                                    <Option value="cancelled">Cancelled</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="Assign To (optional)" name="assignedTo">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select user"
                                    filterOption={(input, opt) =>
                                        opt?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {userOptions.map((u) => (
                                        <Option key={u.value} value={u.value}>{u.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.status !== cur.status}>
                                {({ getFieldValue }) => {
                                    const newStatus = getFieldValue('status');
                                    const needsConfirm = ['resolved', 'cancelled'].includes(newStatus);
                                    return needsConfirm ? (
                                        <Popconfirm
                                            title={`Mark as ${newStatus}?`}
                                            description="This action will record the current user and timestamp."
                                            okText="Confirm"
                                            okButtonProps={{ danger: newStatus === 'cancelled' }}
                                            onConfirm={() => form.submit()}
                                        >
                                            <Button
                                                block
                                                type="primary"
                                                danger={newStatus === 'cancelled'}
                                                loading={updateMutation.isLoading}
                                            >
                                                {newStatus === 'cancelled' ? 'Cancel Follow-up' : 'Mark Resolved'}
                                            </Button>
                                        </Popconfirm>
                                    ) : (
                                        <Button
                                            block
                                            type="primary"
                                            htmlType="submit"
                                            loading={updateMutation.isLoading}
                                        >
                                            Save
                                        </Button>
                                    );
                                }}
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default FollowUpsPage;
